import { Modal, clsx } from "@mantine/core";
import {
  OfferPublished,
  PublishedOfferAuditTrail,
} from "graphql/__generated__/globalTypes";
import { FC, useEffect, useMemo, useState } from "react";

import config from "config";
import { usePublishedOfferAuditTrail } from "hooks/usePublishedOffer";
import { parseUrl } from "utils/helpers";
import AuditTrailEntry from "./AuditTrailEntry";
import PublisherPill from "./PublisherPill";

interface ParticipationHistoryContainerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  offerPublished: OfferPublished;
  offerId: string;
}

const historyTabs = [
  {
    name: "Offer History",
    href: "OFFER_HISTORY",
  },
  {
    name: "Participation History",
    href: "PARTICIPATION_HISTORY",
  },
];

const ParticipationHistoryContainer: FC<ParticipationHistoryContainerProps> = ({
  open,
  setOpen,
  offerPublished,
  offerId,
}) => {
  const [activeTab, setActiveTab] = useState("OFFER_HISTORY");
  const [selectedActor, setSelectedActor] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const {
    auditTrail,
    loading: auditLoading,
    error: auditError,
    refresh,
  } = usePublishedOfferAuditTrail({
    published_offer_id: offerPublished.offer_published_id,
  });

  const [reportContent, setReportContent] = useState<string>("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const uniqueActors = useMemo(() => {
    if (!auditTrail) return [];
    const actors = auditTrail
      .filter((entry): entry is PublishedOfferAuditTrail => entry !== null)
      .map((entry) => entry.actor)
      .filter((actor): actor is string => Boolean(actor));
    return Array.from(new Set(actors)).sort();
  }, [auditTrail]);

  const filteredAuditTrail = useMemo(() => {
    if (!auditTrail) return [];

    return auditTrail
      .filter((entry): entry is PublishedOfferAuditTrail => entry !== null)
      .filter((entry) => {
        if (selectedActor && entry.actor !== selectedActor) return false;

        if (dateFrom || dateTo) {
          const entryDate = entry.created_at
            ? new Date(entry.created_at)
            : null;
          if (!entryDate) return false;

          if (dateFrom && entryDate < new Date(dateFrom)) return false;
          if (dateTo && entryDate > new Date(dateTo + "T23:59:59"))
            return false;
        }

        return true;
      });
  }, [auditTrail, selectedActor, dateFrom, dateTo]);

  const clearFilters = () => {
    setSelectedActor("");
    setDateFrom("");
    setDateTo("");
  };

  useEffect(() => {
    if (open && offerId && activeTab === "OFFER_HISTORY") {
      fetchReportData();
    }
  }, [open, offerId, activeTab]);

  const fetchReportData = async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const response = await fetch(
        parseUrl(`${config.asset.backendUri}/historic/${offerId}`, {
          forceHttps: true,
          defaultProtocol: "https",
        })
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch report: ${response.status} ${response.statusText}`
        );
      }

      const htmlContent = await response.text();
      setReportContent(htmlContent);
    } catch (err) {
      console.error("Error fetching report data:", err);
      setReportError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setReportLoading(false);
    }
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    if (tabKey === "PARTICIPATION_HISTORY") {
      refresh();
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isParticipationAccepted =
    offerPublished.reinsurer_participation?.participation_status === "ACCEPTED";

  const isPlaceItOffer =
    offerPublished.published_by?.toLowerCase() === "placeit";

  const renderOfferHistory = () => {
    if (reportLoading) {
      return (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
        </div>
      );
    }

    if (reportError) {
      return (
        <div className="bg-red-50 p-4 rounded-md">
          <p className="text-red-700">Error loading report: {reportError}</p>
        </div>
      );
    }

    return (
      <div
        className="report-container max-h-96 overflow-y-auto"
        dangerouslySetInnerHTML={{ __html: reportContent }}
      />
    );
  };

  const renderAuditTrail = () => {
    if (auditLoading) {
      return (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
        </div>
      );
    }

    if (auditError) {
      return (
        <div className="text-center py-10">
          <p className="text-red-600">
            Error loading audit trail: {auditError.message}
          </p>
        </div>
      );
    }

    if (!auditTrail || auditTrail.length === 0) {
      return (
        <div className="text-center py-10">
          <p className="text-gray-500">No entries found</p>
        </div>
      );
    }

    return (
      <div>
        <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Actor
              </label>
              <select
                value={selectedActor}
                onChange={(e) => setSelectedActor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All actors</option>
                {uniqueActors.map((actor) => (
                  <option key={actor} value={actor}>
                    {actor}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="flex-1 min-w-32">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear
            </button>
          </div>

          {(selectedActor || dateFrom || dateTo) && (
            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredAuditTrail.length} of{" "}
              {auditTrail.filter((entry) => entry !== null).length} entries
            </div>
          )}
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredAuditTrail.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">
                No entries match the current filters
              </p>
            </div>
          ) : (
            filteredAuditTrail.map((entry, index) => (
              <AuditTrailEntry key={index} entry={entry} index={index} />
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      title="History"
      size="xl"
    >
      <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Offer Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          <p className="text-sm">
            <span className="text-gray-500">Business Name:</span>{" "}
            <span className="font-medium text-gray-800">
              {isPlaceItOffer
                ? offerPublished.offer.class_of_business?.business_name || "N/A"
                : offerPublished.offer.classofbusiness?.business_name || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Insured:</span>{" "}
            <span className="font-medium text-gray-800">
              {offerPublished.offer?.offer_detail?.insured_by || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Fac Offer:</span>{" "}
            <span className="font-medium text-gray-800">
              {offerPublished.offer?.facultative_offer || "N/A"}%
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Policy Number:</span>{" "}
            <span className="font-medium text-gray-800">
              {offerPublished.offer?.offer_detail?.policy_number || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Period:</span>{" "}
            <span className="font-medium text-gray-800">
              {formatDate(
                offerPublished.offer?.offer_detail?.period_of_insurance_from
              )}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-800">
              {formatDate(
                offerPublished.offer?.offer_detail?.period_of_insurance_to
              )}
            </span>
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Published By:</span>
            <PublisherPill publishedBy={offerPublished.published_by} />
          </div>
          {offerPublished.reinsurer_participation && (
            <p className="text-sm md:col-span-2">
              <span className="text-gray-500">
                Your {isParticipationAccepted ? "Accepted" : "Proposed"}{" "}
                Interest:
              </span>{" "}
              <span
                className={clsx(
                  "font-medium",
                  isParticipationAccepted ? "text-green-600" : "text-red-600"
                )}
              >
                {offerPublished?.reinsurer_participation
                  ?.offer_participant_percentage || "N/A"}
                %
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {historyTabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => handleTabChange(tab.href)}
              className={`${
                activeTab === tab.href
                  ? "border-green-500 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              aria-current={activeTab === tab.href ? "page" : undefined}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-2">
        <p className="text-sm text-gray-500">
          {activeTab === "OFFER_HISTORY"
            ? "Complete history and timeline of the above offer"
            : "Complete audit trail of all actions performed on the above offer"}
        </p>
      </div>

      <div className="mt-5 mb-5">
        {activeTab === "OFFER_HISTORY"
          ? renderOfferHistory()
          : renderAuditTrail()}
      </div>
    </Modal>
  );
};

export default ParticipationHistoryContainer;
